import type { ModuleId, ReportData, SubOptionLabelParts } from './data/types';
import { subOptionDefaultOn } from './constants';
import { MODULE_META_EXTRA, MODULE_ORDER, type SubOptionSpec } from './modules/moduleRegistry';

export type SubOptionState = Record<string, boolean>;
export type RailSubOption = {
    id: string;
    label: string;
    labelParts?: SubOptionLabelParts;
    on: boolean;
    available: boolean;
    parentId?: string;
    childrenHeading?: string;
};
export type SubOptionsByModule = Partial<Record<ModuleId, SubOptionState>>;

export const specsFor = (id: ModuleId, data: ReportData): SubOptionSpec[] =>
    MODULE_META_EXTRA[id].subOptions ?? MODULE_META_EXTRA[id].dynamicSubOptions?.(data) ?? [];

const defaultsFor = (specs: SubOptionSpec[], data: ReportData): SubOptionState =>
    Object.fromEntries(specs.map((s) => [s.id, subOptionDefaultOn(s.id) && s.isAvailable(data)]));

export const stateFor = (specs: SubOptionSpec[], data: ReportData, remembered?: SubOptionState): SubOptionState => ({
    ...defaultsFor(specs, data),
    ...remembered,
});

const hasAvailableOn = (specs: SubOptionSpec[], data: ReportData, state?: SubOptionState): boolean =>
    specs.some((s) => s.isAvailable(data) && !!state?.[s.id]);

const contentSpecs = (specs: SubOptionSpec[]) => specs.filter((s) => !s.isDetail);

export const hasUsableSubState = (id: ModuleId, data: ReportData, remembered?: SubOptionState): boolean => {
    const specs = contentSpecs(specsFor(id, data));
    return !specs.length || hasAvailableOn(specs, data, remembered ?? defaultsFor(specs, data));
};

export const seedSubOptions = (data: ReportData): SubOptionsByModule =>
    Object.fromEntries(
        MODULE_ORDER.flatMap((id) => {
            const specs = specsFor(id, data);
            return specs.length ? [[id, defaultsFor(specs, data)]] : [];
        }),
    );

// A card never turns on without a content switch: remembered choices, else defaults, else the first available switch.
export const subStateForTurnOn = (id: ModuleId, data: ReportData, remembered?: SubOptionState): SubOptionState => {
    const specs = specsFor(id, data);
    if (!specs.length) return remembered ?? {};
    const content = contentSpecs(specs);
    const defaults = defaultsFor(specs, data);
    if (!content.length) return remembered ?? defaults;
    if (hasAvailableOn(content, data, remembered)) return remembered ?? {};
    if (hasAvailableOn(content, data, defaults)) return defaults;
    const firstAvailable = content.find((s) => s.isAvailable(data));
    return firstAvailable ? { ...defaults, [firstAvailable.id]: true } : defaults;
};

// What the preview and rail see: a switch is on only if it is remembered on AND still available.
export const resolveSubOptions = (data: ReportData, remembered: SubOptionsByModule): SubOptionsByModule =>
    Object.fromEntries(
        MODULE_ORDER.flatMap((id) => {
            const specs = specsFor(id, data);
            if (!specs.length) return [];
            const state = stateFor(specs, data, remembered[id]);
            return [[id, Object.fromEntries(specs.map((s) => [s.id, s.isAvailable(data) && !!state[s.id]]))]];
        }),
    );

export const toRailSubOptions = (id: ModuleId, data: ReportData, resolved?: SubOptionState): RailSubOption[] | undefined => {
    const specs = specsFor(id, data);
    if (!specs.length) return undefined;
    return specs.map((s) => ({
        id: s.id,
        label: s.label,
        labelParts: s.labelParts,
        on: !!resolved?.[s.id],
        available: s.isAvailable(data),
        ...(s.parentId ? { parentId: s.parentId } : {}),
        ...(s.childrenHeading ? { childrenHeading: s.childrenHeading } : {}),
    }));
};
